import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inventoryService } from '../../services/inventoryService';
import type { InventoryItem, RoastedBulkGroup } from '../../types/inventory';

interface UseRoastedBulkGroupsStateParams {
  nameFilter: string;
  statusFilter: 'all' | 'active' | 'inactive';
  stockStatusFilter: 'all' | 'available' | 'low' | 'depleted';
  items: InventoryItem[];
}

export const useRoastedBulkGroupsState = ({ nameFilter, statusFilter, stockStatusFilter, items }: UseRoastedBulkGroupsStateParams) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const { data: groupsData, isLoading, error } = useQuery({
    queryKey: ['inventory', 'roasted-bulk-groups', { includeItems: true, status: 'all' }],
    queryFn: () => inventoryService.getRoastedBulkGroups(true, { status: 'all' }),
  });

  const groups = useMemo(() => groupsData || [], [groupsData]);
  const itemsIndex = useMemo(() => {
    const map = new Map<string, InventoryItem>();
    items.forEach((item) => map.set(item.id, item));
    return map;
  }, [items]);

  const filteredGroups = useMemo(() => {
    const query = (nameFilter || '').toLowerCase();
    return groups
      .map((group: RoastedBulkGroup) => {
      const itemName = group.itemName || '';
      const itemCode = group.itemCode || '';
      const roastType = group.roastType || '';

      const matchesName =
        itemName.toLowerCase().includes(query) ||
        itemCode.toLowerCase().includes(query) ||
        roastType.toLowerCase().includes(query);

      const item = itemsIndex.get(group.itemId);
      const groupItems = Array.isArray(group.items) ? group.items : undefined;
      const statusFilteredGroupItems = groupItems?.filter(groupItem => {
        const resolvedStatus = itemsIndex.get(groupItem.inventoryItemId)?.status ?? groupItem.status;
        return statusFilter === 'all' || resolvedStatus === statusFilter;
      });

      const fallbackStatusFromGroupItems =
        groupItems
          ?.map(groupItem => itemsIndex.get(groupItem.inventoryItemId)?.status ?? groupItem.status)
          .find(Boolean) ?? undefined;
      const resolvedStatus = item?.status ?? fallbackStatusFromGroupItems;
      const matchesDbStatus = groupItems
        ? statusFilter === 'all' || (statusFilteredGroupItems?.length ?? 0) > 0
        : statusFilter === 'all' || resolvedStatus === statusFilter;

      const resolvedTotalStock = groupItems
        ? (statusFilteredGroupItems ?? []).reduce((sum, groupItem) => sum + (groupItem.remainingQuantity || 0), 0)
        : group.totalStock;

      const derivedStockStatus = (() => {
        if (resolvedTotalStock <= 0) return 'depleted';
        const reorderPoint = typeof item?.reorderPoint === 'number' ? item.reorderPoint : 0;
        if (reorderPoint > 0 && resolvedTotalStock <= reorderPoint) return 'low';
        return 'available';
      })();
      const matchesStockStatus = stockStatusFilter === 'all' || derivedStockStatus === stockStatusFilter;

      if (!matchesName || !matchesDbStatus || !matchesStockStatus) {
        return null;
      }

      return {
        ...group,
        ...(groupItems ? { items: statusFilteredGroupItems, totalStock: resolvedTotalStock } : {}),
      };
    })
      .filter((group): group is RoastedBulkGroup => group !== null);
  }, [groups, nameFilter, statusFilter, stockStatusFilter, itemsIndex]);

  const toggleGroup = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return {
    filteredGroups,
    expandedKeys,
    toggleGroup,
    isLoading,
    error,
  };
};
